import { useQuery } from "@tanstack/react-query";
import {
  getAllFeatureFlags,
  getFeatureFlagByKey,
  getFeatureFlagsByCategory,
} from "../../services/featureFlagsService";

// Default feature flags (fallback when API fails)
const DEFAULT_FEATURE_FLAGS = {
  mainFeatures: {
    autostack: { web: false },
    buyCrypto: { web: false },
    buyForex: { web: false },
    deposit: { web: false },
    eightPay: { web: false },
    invest: { web: false },
    zar8: { web: false },
    zar8Yield: { web: false },
    receiveCrypto: { web: false },
    depositForex: { web: false },
    sellCrypto: { web: false },
    sellForex: { web: false },
    sendCrypto: { web: false },
    withdrawForex: { web: false },
    withdraw: { web: false },
  },
  supportPage: {
    searchBar: { web: false },
    whatsapp: { web: false },
    email: { web: false },
    faqs: { web: false },
    chat: { web: false },
  },
  settingsPage: {
    donations: { web: false },
    subAccounts: { web: false },
    corporateAccount: { web: false },
    becomeABroker: { web: false },
    inviteTo80eight: { web: false },
    priceAlerts: { web: false },
    addressBook: { web: false },
    wallets: { web: false },
    preferences: { web: false },
    security: { web: false },
    notifications: { web: false },
    about: { web: false },
    profile: { web: false },
  },
};

// Cache times
const FEATURE_FLAG_SINGLE_CACHE = 1000 * 60 * 5; // 5 minutes
const FEATURE_FLAG_ALL_CACHE = 1000 * 60 * 30; // 30 minutes

const getDefaultFeatureFlagCategory = (category) => {
  switch (category) {
    case "mainFeatures":
      return DEFAULT_FEATURE_FLAGS.mainFeatures;
    case "supportPage":
      return DEFAULT_FEATURE_FLAGS.supportPage;
    case "settingsPage":
      return DEFAULT_FEATURE_FLAGS.settingsPage;
    default:
      return {};
  }
};

const getDefaultCategoryFlags = (category) =>
  Object.fromEntries(
    Object.entries(getDefaultFeatureFlagCategory(category)).map(
      ([key, value]) => [key, value?.web ?? false]
    )
  );

const getDefaultFeatureFlagValue = (category, key) => {
  const flagEntry = Object.entries(
    getDefaultFeatureFlagCategory(category)
  ).find(([flagKey]) => flagKey === key);

  return flagEntry?.[1]?.web ?? false;
};

/**
 * Hook to get ALL feature flags with 30 minute cache
 * Use this when you need access to multiple flags or want to cache everything
 */
export const useAllFeatureFlags = () => {
  return useQuery({
    queryKey: ["allFeatureFlags"],
    queryFn: async () => {
      try {
        const response = await getAllFeatureFlags();
        return response?.data?.data || DEFAULT_FEATURE_FLAGS;
      } catch (error) {
        console.error("Error fetching all feature flags:", error);
        return DEFAULT_FEATURE_FLAGS;
      }
    },
    staleTime: FEATURE_FLAG_ALL_CACHE,
    gcTime: FEATURE_FLAG_ALL_CACHE + 1000 * 60 * 5,
  });
};

/**
 * Hook to get a SINGLE feature flag with 5 minute cache
 * Use this on individual pages to fetch only what's needed
 * @param {string} category - "mainFeatures" or "settingsPage"
 * @param {string} key - Feature flag key (e.g., "buyCrypto", "profile")
 */
export const useFeatureFlag = (category, key) => {
  return useQuery({
    queryKey: ["featureFlag", category, key],
    queryFn: async () => {
      const fallbackValue = getDefaultFeatureFlagValue(category, key);

      try {
        const response = await getFeatureFlagByKey(category, key);
        return response?.data?.data?.enabled ?? fallbackValue;
      } catch (error) {
        console.error(`Error fetching feature flag ${category}/${key}:`, error);
        return fallbackValue;
      }
    },
    enabled: !!category && !!key,
    staleTime: FEATURE_FLAG_SINGLE_CACHE,
    gcTime: FEATURE_FLAG_SINGLE_CACHE + 1000 * 60 * 2,
  });
};

/**
 * Hook to get feature flags by category with 5 minute cache
 * @param {string} category - "mainFeatures" or "settingsPage"
 * @returns {Object} flags - Simple boolean map { flagKey: true/false }
 */
export const useFeatureFlagsByCategory = (category) => {
  return useQuery({
    queryKey: ["featureFlagsCategory", category],
    queryFn: async () => {
      const defaultFlags = getDefaultCategoryFlags(category);

      try {
        const response = await getFeatureFlagsByCategory(category);
        const data = response?.data?.data || {};
        // Transform to simple boolean map using Object.entries for safe iteration
        const responseFlags = Object.fromEntries(
          Object.entries(data).map(([key, value]) => [key, value?.web ?? false])
        );

        return {
          ...defaultFlags,
          ...responseFlags,
        };
      } catch (error) {
        console.error(`Error fetching feature flags for ${category}:`, error);
        return defaultFlags;
      }
    },
    enabled: !!category,
    staleTime: FEATURE_FLAG_SINGLE_CACHE,
    gcTime: FEATURE_FLAG_SINGLE_CACHE + 1000 * 60 * 2,
  });
};
